import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Layout, Plus, ChevronLeft, ChevronRight, Youtube, Edit, Trash2, X, Save, Eye, EyeOff } from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader, formatDateTime, formatTime12Hour } from "./utils";

export const ScheduleTab = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [broadcasts, setBroadcasts] = useState([]);
  const [viewMode, setViewMode] = useState("calendar");
  const [channels, setChannels] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingBroadcast, setEditingBroadcast] = useState(null);
  const [formData, setFormData] = useState({
    title: "", sport: "Football", channel_id: "channel1", scheduled_date: "", scheduled_time: "", description: "",
    youtube_url: "",
    home_team: { name: "", mascot: "", color: "#FFFFFF", logo_url: "" },
    away_team: { name: "", mascot: "", color: "#FFFFFF", logo_url: "" },
    external_links: []
  });
  const [saving, setSaving] = useState(false);
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [calendarRes, channelsRes] = await Promise.all([
          axios.get(`${API}/broadcasts/calendar/${year}/${month + 1}`),
          axios.get(`${API}/channels`)
        ]);
        setBroadcasts(calendarRes.data);
        setChannels(channelsRes.data);
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, [year, month]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingBroadcast) {
        // Update existing broadcast
        await axios.put(`${API}/broadcasts/${editingBroadcast.id}`, formData, { headers: getAuthHeader() });
      } else {
        // Create new broadcast
        await axios.post(`${API}/broadcasts/schedule`, formData, { headers: getAuthHeader() });
      }
      setShowForm(false);
      setEditingBroadcast(null);
      setFormData({
        title: "", sport: "Football", channel_id: "channel1", scheduled_date: "", scheduled_time: "", description: "",
        youtube_url: "",
        home_team: { name: "", mascot: "", color: "#FFFFFF", logo_url: "" },
        away_team: { name: "", mascot: "", color: "#FFFFFF", logo_url: "" },
        external_links: []
      });
      const response = await axios.get(`${API}/broadcasts/calendar/${year}/${month + 1}`);
      setBroadcasts(response.data);
    } catch (error) { console.error("Error:", error); } 
    finally { setSaving(false); }
  };

  const handleEdit = (broadcast) => {
    setEditingBroadcast(broadcast);
    setFormData({
      title: broadcast.title || "",
      sport: broadcast.sport || "Football",
      channel_id: broadcast.channel_id || "channel1",
      scheduled_date: broadcast.scheduled_date || "",
      scheduled_time: broadcast.scheduled_time || "",
      description: broadcast.description || "",
      youtube_url: broadcast.youtube_url || "",
      home_team: broadcast.home_team || { name: "", mascot: "", color: "#FFFFFF", logo_url: "" },
      away_team: broadcast.away_team || { name: "", mascot: "", color: "#FFFFFF", logo_url: "" },
      external_links: broadcast.external_links || []
    });
    setShowForm(true);
  };

  const handleDelete = async (broadcastId) => {
    if (!window.confirm("Are you sure you want to delete this broadcast?")) return;
    try {
      await axios.delete(`${API}/broadcasts/${broadcastId}`, { headers: getAuthHeader() });
      const response = await axios.get(`${API}/broadcasts/calendar/${year}/${month + 1}`);
      setBroadcasts(response.data);
    } catch (error) {
      console.error("Error deleting broadcast:", error);
    }
  };

  const handleToggleHidden = async (broadcastId, hidden) => {
    try {
      await axios.put(`${API}/broadcasts/schedule/${broadcastId}`, { is_hidden: hidden }, { headers: getAuthHeader() });
      const response = await axios.get(`${API}/broadcasts/calendar/${year}/${month + 1}`);
      setBroadcasts(response.data);
    } catch (error) {
      console.error("Error toggling visibility:", error);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingBroadcast(null);
    setFormData({
      title: "", sport: "Football", channel_id: "channel1", scheduled_date: "", scheduled_time: "", description: "",
      youtube_url: "",
      home_team: { name: "", mascot: "", color: "#FFFFFF", logo_url: "" },
      away_team: { name: "", mascot: "", color: "#FFFFFF", logo_url: "" },
      external_links: []
    });
  };

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const getBroadcastsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return broadcasts.filter(b => b.scheduled_date === dateStr);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">Schedule</h2>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex bg-zinc-800 border border-white/10">
            <button
              onClick={() => setViewMode("calendar")}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest ${viewMode === "calendar" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}
            >
              <Calendar className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest ${viewMode === "grid" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}
            >
              <Layout className="w-4 h-4" />
            </button>
          </div>
          <Button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-secondary font-bold uppercase tracking-widest text-xs px-4 py-2 h-auto rounded-none">
            <Plus className="w-4 h-4" /> Add Broadcast
          </Button>
        </div>
      </div>

      {/* Add/Edit Broadcast Form */}
      {showForm && (
        <motion.form initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleCreate} className="p-6 bg-zinc-900/50 border border-white/10 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-secondary text-lg font-bold uppercase text-white">
              {editingBroadcast ? "Edit Broadcast" : "Schedule New Broadcast"}
            </h3>
            <button type="button" onClick={handleCancel} className="text-zinc-500 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Title *</label>
              <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required placeholder="Game Title" className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Sport *</label>
              <select value={formData.sport} onChange={(e) => setFormData({ ...formData, sport: e.target.value })} className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm">
                {["Football", "Basketball", "Soccer", "Baseball", "Volleyball", "Wrestling"].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Channel *</label>
              <select value={formData.channel_id} onChange={(e) => setFormData({ ...formData, channel_id: e.target.value })} className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm">
                {channels.map((ch) => <option key={ch.channel_id} value={ch.channel_id}>{ch.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Date *</label>
              <input type="date" value={formData.scheduled_date} onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })} required className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Time *</label>
              <input type="time" value={formData.scheduled_time} onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })} required className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Description</label>
              <input type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" />
            </div>
          </div>
          
          {/* YouTube URL Field */}
          <div className="border-t border-white/10 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Youtube className="w-5 h-5 text-red-500" />
              <h4 className="font-secondary text-sm font-bold uppercase text-white">Stream Source</h4>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">YouTube Live URL</label>
              <input 
                type="text" 
                value={formData.youtube_url} 
                onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })} 
                placeholder="https://www.youtube.com/watch?v=... or https://youtube.com/live/..."
                className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                data-testid="youtube-url-input"
              />
              <p className="text-[10px] text-zinc-600 mt-1">Paste your YouTube Live stream URL. The video will play with ASN&apos;s custom player (YouTube controls hidden).</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Button type="submit" disabled={saving} className="bg-white text-black hover:bg-zinc-200 font-secondary font-bold uppercase tracking-widest text-xs px-6 py-2 h-auto rounded-none flex items-center gap-2">
              {editingBroadcast ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {saving ? "Saving..." : editingBroadcast ? "Update Broadcast" : "Create Broadcast"}
            </Button>
            <button type="button" onClick={handleCancel} className="text-zinc-500 text-sm hover:text-white">Cancel</button>
          </div>
        </motion.form>
      )}

      {/* Calendar View */}
      {viewMode === "calendar" && (
        <div className="bg-zinc-900/50 border border-white/5 p-6">
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => setCurrentDate(new Date(year, month - 1))} className="p-2 text-zinc-400 hover:text-white"><ChevronLeft className="w-5 h-5" /></button>
            <h3 className="font-secondary text-xl font-bold text-white">{monthNames[month]} {year}</h3>
            <button onClick={() => setCurrentDate(new Date(year, month + 1))} className="p-2 text-zinc-400 hover:text-white"><ChevronRight className="w-5 h-5" /></button>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
              <div key={day} className="text-center text-xs font-bold uppercase tracking-widest text-zinc-500 py-2">{day}</div>
            ))}
            {[...Array(firstDay)].map((_, i) => <div key={`e-${i}`} />)}
            {[...Array(daysInMonth)].map((_, i) => {
              const day = i + 1;
              const dayBroadcasts = getBroadcastsForDay(day);
              const isToday = new Date().getDate() === day && new Date().getMonth() === month && new Date().getFullYear() === year;
              return (
                <div key={day} className={`min-h-[80px] p-2 border ${isToday ? 'border-white/30 bg-white/5' : 'border-white/5'} ${dayBroadcasts.length > 0 ? 'bg-zinc-800/50' : ''}`}>
                  <div className={`text-xs mb-1 ${isToday ? 'text-white font-bold' : 'text-zinc-400'}`}>{day}</div>
                  {dayBroadcasts.slice(0, 2).map((b, idx) => (
                    <div key={idx} className={`text-[9px] truncate px-1 py-0.5 mb-0.5 ${b.is_live ? 'bg-red-600 text-white' : 'bg-zinc-700 text-zinc-300'}`}>
                      {formatTime12Hour(b.scheduled_time)} {b.title}
                    </div>
                  ))}
                  {dayBroadcasts.length > 2 && (
                    <div className="text-[9px] text-zinc-500">+{dayBroadcasts.length - 2} more</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Grid/Table View */}
      {viewMode === "grid" && (
        <div className="bg-zinc-900/50 border border-white/5">
          {/* Table Header */}
          <div className="grid grid-cols-7 gap-4 p-4 border-b border-white/10 text-xs font-bold uppercase tracking-widest text-zinc-500">
            <div>Title</div>
            <div>Sport</div>
            <div>Date & Time</div>
            <div>Channel</div>
            <div>Status</div>
            <div>Stream</div>
            <div>Actions</div>
          </div>
          
          {/* Table Rows */}
          {broadcasts.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">No broadcasts scheduled for this month</div>
          ) : (
            broadcasts.sort((a, b) => new Date(`${a.scheduled_date}T${a.scheduled_time || '00:00'}`) - new Date(`${b.scheduled_date}T${b.scheduled_time || '00:00'}`)).map((broadcast) => (
              <div key={broadcast.id} className={`grid grid-cols-7 gap-4 p-4 border-b border-white/5 items-center ${broadcast.is_hidden ? 'bg-yellow-500/5 opacity-60' : broadcast.is_live ? 'bg-red-500/10' : 'hover:bg-zinc-800/50'}`}>
                <div>
                  <span className="font-secondary text-sm font-bold text-white">{broadcast.title}</span>
                </div>
                <div className="text-sm text-zinc-400">{broadcast.sport}</div>
                <div className="text-sm text-zinc-400">{formatDateTime(broadcast.scheduled_date, broadcast.scheduled_time)}</div>
                <div className="text-sm text-zinc-400">{channels.find(c => c.channel_id === broadcast.channel_id)?.name || broadcast.channel_id}</div>
                <div>
                  {broadcast.is_live ? (
                    <span className="flex items-center gap-1.5 px-2 py-0.5 bg-red-600 text-white text-[10px] font-black uppercase w-fit">
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>LIVE
                    </span>
                  ) : broadcast.is_completed ? (
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-500 text-[10px] font-bold uppercase">Final</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-zinc-700 text-zinc-300 text-[10px] font-bold uppercase">Scheduled</span>
                  )}
                </div>
                <div>
                  {broadcast.youtube_url ? (
                    <span className="flex items-center gap-1 text-red-500 text-xs">
                      <Youtube className="w-3 h-3" /> YouTube
                    </span>
                  ) : (
                    <span className="text-zinc-600 text-xs">No source</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleHidden(broadcast.id, !broadcast.is_hidden)}
                    className={`p-1.5 transition-colors ${broadcast.is_hidden ? 'text-yellow-500 hover:text-white' : 'text-zinc-500 hover:text-yellow-500'}`}
                    title={broadcast.is_hidden ? 'Show to public' : 'Hide from public'}
                    data-testid={`toggle-hidden-${broadcast.id}`}
                  >
                    {broadcast.is_hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button 
                    onClick={() => handleEdit(broadcast)}
                    className="p-1.5 text-zinc-500 hover:text-white transition-colors"
                    title="Edit broadcast"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleDelete(broadcast.id)}
                    className="p-1.5 text-zinc-500 hover:text-red-500 transition-colors"
                    title="Delete broadcast"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </motion.div>
  );
};

export default ScheduleTab;
